import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  AssessmentOutlined,
  AutoGraphOutlined,
  ContentCutOutlined,
  ErrorOutlineOutlined,
  HealthAndSafetyOutlined,
  InsightsOutlined,
  InventoryOutlined,
  LocalAtmOutlined,
  PointOfSaleOutlined,
  RefreshOutlined,
  ReportProblemOutlined,
  ShowChartOutlined,
  SpeedOutlined,
  TrendingUpOutlined,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useAuth } from '../context/AuthContext';
import { homeService } from '../services';

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(-2deg); }
  50%       { transform: translateY(-8px) rotate(2deg); }
`;

const TONE = {
  positive: 'success',
  success: 'success',
  warning: 'warning',
  danger: 'error',
  info: 'info',
  neutral: 'default',
};

const KPI_ICONS = {
  monthly_sales: AutoGraphOutlined,
  sales_pace: TrendingUpOutlined,
  profit: LocalAtmOutlined,
  grooming_income: ContentCutOutlined,
  ticket_avg: ShowChartOutlined,
  grooming_services: ContentCutOutlined,
  grooming_completed: ContentCutOutlined,
  inventory_pressure: InventoryOutlined,
  inventory_approvals: ReportProblemOutlined,
  cash_status: PointOfSaleOutlined,
  cash_pending: PointOfSaleOutlined,
  sales_activity: TrendingUpOutlined,
  customers_recovery: ReportProblemOutlined,
};


// Paleta más suave y profesional
const CHART_COLORS = ['#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981', '#f43f5e', '#6366f1'];

// Función para formatear números grandes
const formatCompactNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return '0';
  const n = Number(num);
  if (Math.abs(n) >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatMoneyGT = (value) => {
  if (value === null || value === undefined || value === '') return 'Q 0.00';
  if (typeof value === 'string') {
    const cleaned = value.replace(/[Q,\s]/g, '');
    const parsed = Number(cleaned);
    if (Number.isFinite(parsed)) {
      return `Q ${parsed.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? `Q ${parsed.toLocaleString('es-GT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : 'Q 0.00';
};

const MONETARY_KPI_KEYS = new Set([
  'monthly_sales',
  'sales_pace',
  'profit',
  'grooming_income',
  'ticket_avg',
]);

// Función para truncar texto largo
const truncateText = (text, maxLength = 25) => {
  if (!text || typeof text !== 'string') return text;
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
};

const capitalizeWord = (value) => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  return raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
};

const getHeroName = (user) => {
  const firstName = String(user?.first_name || '').trim();
  if (firstName) return capitalizeWord(firstName.split(/\s+/).filter(Boolean)[0]);

  const username = String(user?.username || '').trim();
  if (username) return capitalizeWord(username.split(/[\s._-]+/).filter(Boolean)[0] || username);

  return 'Usuario';
};

const safeArray = (value) => (Array.isArray(value) ? value : []);
const chartValueKey = (rows) => (safeArray(rows).some((row) => Number(row?.total || 0) > 0) ? 'total' : 'count');
const HOME_SECTION_WRAP_SX = {
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',
  px: { xs: 0.6, sm: 0.8, md: 1 },
};

const getDashboardMode = (payload) => payload?.mode || (payload?.admin_dashboard ? 'admin' : 'admin');
const getAdminDashboard = (payload) => (payload?.mode === 'admin' ? payload?.admin_dashboard : payload);
const getOperativeHome = (payload) => (payload?.mode === 'operative' ? payload?.operative_home : null);

const formatDateTimeCompact = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-GT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

function HeroCompact({ greeting, name }) {
  const theme = useTheme();

  const timeCtx = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) {
      return {
        from: theme.palette.primary.dark,
        via: '#c56a21',
        to: '#f59e0b',
        emoji: '☀️',
      };
    }
    if (h < 19) {
      return {
        from: theme.palette.primary.main,
        via: theme.palette.primary.dark,
        to: '#6F130F',
        emoji: '🌤️',
      };
    }
    return {
      from: '#0f172a',
      via: theme.palette.primary.dark,
      to: '#312e81',
      emoji: '🌙',
    };
  }, [theme]);

  return (
    <Box
      sx={{
        borderRadius: 3,
        p: { xs: 3, md: 4 },
        position: 'relative',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${timeCtx.from} 0%, ${timeCtx.via} 55%, ${timeCtx.to} 100%)`,
        boxShadow: `0 8px 32px ${timeCtx.from}55`,
        animation: `${fadeUp} 0.4s ease both`,
        minHeight: { xs: 160, md: 180 },
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Box sx={{ position: 'absolute', top: -30, right: -30, width: 180, height: 180, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.08)' }} />
      <Box sx={{ position: 'absolute', top: 20, right: 60, width: 80, height: 80, borderRadius: '50%', border: '1.5px solid rgba(255,255,255,0.06)' }} />
      <Box sx={{ position: 'absolute', bottom: -40, left: '30%', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.04)' }} />

      <Box
        sx={{
          position: 'absolute',
          right: { xs: 20, md: 48 },
          top: '50%',
          transform: 'translateY(-50%)',
          fontSize: { xs: 48, md: 64 },
          lineHeight: 1,
          animation: `${float} 4s ease-in-out infinite`,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {timeCtx.emoji}
      </Box>

      <Stack spacing={0.75} sx={{ position: 'relative', zIndex: 1 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)' }}>
          {greeting}
        </Typography>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.6rem', md: '2rem' }, lineHeight: 1.1, color: '#fff', textShadow: '0 2px 12px rgba(0,0,0,0.25)' }}>
          {name || 'Usuario'}
        </Typography>
        <Typography sx={{ fontSize: 13, color: 'rgba(255,255,255,0.6)', mt: 0.5 }}>
          {new Date().toLocaleDateString('es-GT', { weekday: 'long', day: 'numeric', month: 'long' })}
        </Typography>
      </Stack>
    </Box>
  );
}

function DashboardCard({ children, sx, delay = 0 }) {
  const theme = useTheme();
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 3,
        borderColor: alpha(theme.palette.divider, 0.45),
        background: `linear-gradient(145deg, ${alpha(theme.palette.background.paper, 0.97)}, ${alpha(theme.palette.primary.main, 0.035)})`,
        boxShadow: '0 18px 46px rgba(15,23,42,0.065)',
        overflow: 'hidden',
        animation: `${fadeUp} 0.45s ease both`,
        animationDelay: `${delay}ms`,
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 24px 60px rgba(15,23,42,0.085)',
          borderColor: alpha(theme.palette.primary.main, 0.2),
        },
        ...sx,
      }}
    >
      {children}
    </Card>
  );
}

function EmptyState({ title = 'Sin actividad en el periodo', caption = 'El panel conservará este espacio cuando existan datos.' }) {
  const theme = useTheme();
  return (
    <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ minHeight: 180, borderRadius: 2.5, border: `1px dashed ${alpha(theme.palette.text.primary, 0.15)}`, bgcolor: alpha(theme.palette.text.primary, 0.025), p: 2 }}>
      <InsightsOutlined sx={{ color: 'text.disabled', fontSize: 30 }} />
      <Typography sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="body2" color="text.secondary" textAlign="center">{caption}</Typography>
    </Stack>
  );
}

function DashboardTopBar({ data, loading, onRefresh }) {
  const theme = useTheme();
  const reports = Boolean(data?.permissions?.can_view_reports);
  return (
    <DashboardCard delay={50} sx={{ background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.09)}, ${alpha(theme.palette.info.main, 0.045)} 55%, ${alpha(theme.palette.warning.main, 0.055)})` }}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box sx={{ width: 48, height: 48, borderRadius: 2.3, display: 'grid', placeItems: 'center', color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1), boxShadow: `inset 0 0 0 1px ${alpha(theme.palette.primary.main, 0.14)}` }}>
              <SpeedOutlined />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: { xs: 20, md: 24 }, letterSpacing: '-0.03em' }}>Panel del mes</Typography>
              <Typography color="text.secondary" sx={{ fontSize: 13.5 }}>
                {data?.period?.label || 'Mes actual'} · {data?.summary?.subheadline || 'Cargando resumen operativo'}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {reports ? <Chip size="small" color="info" variant="outlined" label="Vista administrativa" /> : <Chip size="small" variant="outlined" label="Vista operativa" />}
            {data?.summary?.visible_areas != null ? <Chip size="small" variant="outlined" label={`${data.summary.visible_areas} áreas visibles`} /> : null}
            <Typography variant="caption" color="text.secondary">
              {data?.summary?.last_updated ? `Actualizado ${new Date(data.summary.last_updated).toLocaleTimeString('es-GT', { hour: '2-digit', minute: '2-digit' })}` : ''}
            </Typography>
            <Tooltip title="Actualizar">
              <span>
                <IconButton size="small" onClick={onRefresh} disabled={loading} sx={{ border: `1px solid ${alpha(theme.palette.divider, 0.7)}` }}>
                  {loading ? <CircularProgress size={16} /> : <RefreshOutlined fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}

function KpiCard({ item, index }) {
  const theme = useTheme();
  const Icon = KPI_ICONS[item?.key] || AssessmentOutlined;
  const colorKey = TONE[item?.tone] || 'default';
  const color = colorKey === 'default' ? theme.palette.text.secondary : theme.palette[colorKey].main;

  const rawValue = item?.raw_value ?? item?.value;
  const isMonetary =
    MONETARY_KPI_KEYS.has(item?.key) ||
    (item?.sensitive && typeof item?.value === 'string' && item.value.trim().startsWith('Q'));
  const displayValue = isMonetary ? formatMoneyGT(rawValue) : rawValue;

  return (
    <DashboardCard delay={90 + index * 35}>
      <CardContent sx={{ px: { xs: 1.8, sm: 2.1 }, py: 2.1, '&:last-child': { pb: 2.1 } }}>
        <Stack spacing={1.4}>
          <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
            <Box sx={{ width: 44, height: 44, borderRadius: '50%', display: 'grid', placeItems: 'center', color, bgcolor: alpha(color, 0.11), boxShadow: `inset 0 0 0 1px ${alpha(color, 0.16)}` }}>
              <Icon sx={{ fontSize: 22 }} />
            </Box>
            <Chip size="small" variant="outlined" color={colorKey === 'default' ? 'default' : colorKey} label={item?.sensitive ? 'Reportes' : 'Operativo'} sx={{ height: 23, fontWeight: 600, fontSize: 11 }} />
          </Stack>
          <Box sx={{ minWidth: 0 }}>
            <Tooltip title={truncateText(item?.label, 50) || ''} placement="top">
              <Typography sx={{ fontWeight: 600, fontSize: 14.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {item?.label}
              </Typography>
            </Tooltip>
            <Tooltip title={item?.value || ''} placement="top">
              <Typography sx={{ mt: 0.55, fontWeight: 700, fontSize: { xs: 23, sm: 25, md: 27 }, letterSpacing: '-0.035em', lineHeight: 1.08, overflowWrap: 'anywhere' }}>
                {displayValue}
              </Typography>
            </Tooltip>
            <Typography color="text.secondary" sx={{ mt: 0.3, fontSize: 12.6, minHeight: 34, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
              {truncateText(item?.delta_label, 60) || 'Dato agregado del periodo'}
            </Typography>
          </Box>
          <Box sx={{ height: 5, borderRadius: 99, bgcolor: alpha(theme.palette.text.primary, 0.06), overflow: 'hidden' }}>
            <Box sx={{ width: `${Math.min(100, Math.max(18, Math.abs(Number(item?.delta || item?.raw_value || 42)) % 100))}%`, height: '100%', bgcolor: alpha(color, 0.72), borderRadius: 99 }} />
          </Box>
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}

function KpiGrid({ kpis, loading }) {
  if (loading) {
    return (
      <Grid container spacing={1.5}>
        {[0, 1, 2, 3, 4, 5].map((key) => (
          <Grid item xs={12} sm={6} lg={4} xl={2} key={key}>
            <DashboardCard><CardContent><Skeleton width={44} height={44} variant="circular" /><Skeleton sx={{ mt: 2 }} /><Skeleton width="70%" height={34} /><Skeleton /></CardContent></DashboardCard>
          </Grid>
        ))}
      </Grid>
    );
  }

  return (
    <Grid container spacing={1.5}>
      {safeArray(kpis).map((item, index) => (
        <Grid item xs={12} sm={6} lg={4} xl={2} key={item.key || item.label}>
          <KpiCard item={item} index={index} />
        </Grid>
      ))}
    </Grid>
  );
}

function MainTrendChart({ rows, reports }) {
  const theme = useTheme();
  const valueKey = chartValueKey(rows);
  const hasData = safeArray(rows).some((row) => Number(row?.[valueKey] || 0) > 0);

  return (
    <DashboardCard delay={180} sx={{ minHeight: 410 }}>
      <CardContent sx={{ p: { xs: 2, md: 2.6 }, '&:last-child': { pb: { xs: 2, md: 2.6 } } }}>
        <Stack spacing={2}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 19, letterSpacing: '-0.02em' }}>{reports ? 'Tendencia de rendimiento' : 'Actividad visible por día'}</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 13.2 }}>Datos agregados del mes actual, sin registros crudos.</Typography>
          </Box>
          {hasData ? (
            <Box sx={{ height: 305 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={rows} margin={{ top: 10, right: 14, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="homeTrend" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS[0]} stopOpacity={0.28} />
                      <stop offset="95%" stopColor={CHART_COLORS[0]} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.text.primary, 0.09)} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} minTickGap={8} />
                  <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} width={44} tickFormatter={formatCompactNumber} />
                  <ChartTooltip formatter={(value) => {
                    if (reports && valueKey === 'total') {
                      return [`Q ${Number(value).toLocaleString('es-GT', { minimumFractionDigits: 2 })}`, 'Total'];
                    }
                    return [Number(value).toLocaleString(), 'Cantidad'];
                  }} labelFormatter={(label) => `Día ${label}`} />
                  <Area type="monotone" dataKey={valueKey} stroke={CHART_COLORS[0]} fill="url(#homeTrend)" strokeWidth={2.5} dot={{ r: 2.5 }} activeDot={{ r: 5 }} />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          ) : <EmptyState caption="No hay ventas completadas visibles en el periodo seleccionado." />}
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}

function CompactChartCard({ title, subtitle, rows, kind = 'bar', valueKey: forcedKey, delay = 220 }) {
  const theme = useTheme();
  const valueKey = forcedKey || chartValueKey(rows);
  const hasData = safeArray(rows).some((row) => Number(row?.[valueKey] || 0) > 0);

  return (
    <DashboardCard delay={delay}>
      <CardContent sx={{ p: 2.2, '&:last-child': { pb: 2.2 } }}>
        <Stack spacing={1.6}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{title}</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12.6 }}>{subtitle}</Typography>
          </Box>
          {hasData ? (
            <Box sx={{ height: 178 }}>
              <ResponsiveContainer width="100%" height="100%">
                {kind === 'pie' ? (
                  <PieChart>
                    <Pie data={rows} dataKey={valueKey} nameKey="label" innerRadius={48} outerRadius={72} paddingAngle={3}>
                      {rows.map((entry, index) => <Cell key={entry.label} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                    </Pie>
                    <ChartTooltip formatter={(value, name) => [formatCompactNumber(value), name]} />
                  </PieChart>
                ) : (
                  <BarChart data={rows} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                    <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 10.5, fill: theme.palette.text.secondary }} interval={0} />
                    <YAxis tickLine={false} axisLine={false} tick={{ fontSize: 10.5, fill: theme.palette.text.secondary }} tickFormatter={formatCompactNumber} />
                    <ChartTooltip formatter={(value) => [formatCompactNumber(value), title]} />
                    <Bar dataKey={valueKey} radius={[8, 8, 3, 3]} fill={theme.palette.primary.main}>
                      {rows.map((entry, index) => <Cell key={entry.label} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Box>
          ) : <EmptyState title="Sin datos" caption="No hay actividad agregada para esta sección." />}
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}

function AgendaGroomingCard({ metrics, delay = 260 }) {
  const theme = useTheme();
  const rows = safeArray(metrics?.today_agenda).slice(0, 3);

  return (
    <DashboardCard delay={delay}>
      <CardContent sx={{ p: 2.2, '&:last-child': { pb: 2.2 } }}>
        <Stack spacing={1.6}>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: 16 }}>Agenda grooming de hoy</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12.6 }}>
              {metrics?.today_count || 0} citas hoy · {metrics?.in_progress_count || 0} en proceso · {metrics?.completed_count || 0} finalizadas
            </Typography>
          </Box>
          {rows.length ? (
            <Stack spacing={1.1}>
              {rows.map((row, index) => (
                <Stack key={`${row.time}-${row.pet}-${index}`} direction="row" spacing={1.2} alignItems="flex-start">
                  <Stack alignItems="center" sx={{ pt: 0.2 }}>
                    <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: theme.palette.info.main }} />
                    {index < rows.length - 1 ? <Box sx={{ width: 1, height: 36, bgcolor: alpha(theme.palette.info.main, 0.25), mt: 0.5 }} /> : null}
                  </Stack>
                  <Box sx={{ minWidth: 54 }}>
                    <Typography sx={{ fontWeight: 900, fontSize: 13 }}>{row.time}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Stack direction="row" spacing={0.8} alignItems="center" justifyContent="space-between">
                      <Typography noWrap sx={{ fontWeight: 850, fontSize: 13.5 }}>{row.pet}</Typography>
                      <Chip size="small" label={row.status_label || row.status} variant="outlined" sx={{ height: 20, fontSize: 10.5 }} />
                    </Stack>
                    <Typography noWrap color="text.secondary" sx={{ fontSize: 12.2 }}>
                      {row.customer} · {row.service}
                    </Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          ) : (
            <EmptyState title="Sin citas hoy" caption="No hay citas grooming programadas para hoy." />
          )}
          {metrics?.grooming_sales_total ? (
            <Typography variant="caption" color="text.secondary">
              Ingresos grooming del mes: <strong>{metrics.grooming_sales_total}</strong>
            </Typography>
          ) : null}
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}
function ChartsSection({ data }) {
  const reports = Boolean(data?.permissions?.can_view_reports);
  const charts = data?.charts || {};
  const paymentChart = safeArray(charts.sales_by_payment_method).length
    ? { title: 'Métodos de pago', subtitle: reports ? 'Monto cobrado por método' : 'Transacciones por método', rows: charts.sales_by_payment_method, kind: 'pie', valueKey: reports ? 'total' : 'count' }
    : { title: 'Productos vs grooming', subtitle: reports ? 'Ingreso por línea de negocio' : 'Actividad por línea de negocio', rows: charts.sales_by_line, kind: 'bar', valueKey: reports ? 'total' : 'count' };
  const secondaryChart = safeArray(charts.sales_by_branch).length > 1
    ? { title: 'Ventas por sucursal', subtitle: reports ? 'Top sucursales por ingreso' : 'Top sucursales por actividad', rows: charts.sales_by_branch, kind: 'bar', valueKey: reports ? 'total' : 'count' }
    : { title: 'Productos vs grooming', subtitle: reports ? 'Ingreso por línea de negocio' : 'Actividad por línea de negocio', rows: charts.sales_by_line, kind: 'bar', valueKey: reports ? 'total' : 'count' };

  return (
    <Grid container spacing={1.5} alignItems="stretch">
      <Grid item xs={12} lg={8}>
        <MainTrendChart rows={charts.sales_by_day} reports={reports} />
      </Grid>
      <Grid item xs={12} lg={4}>
        <Stack spacing={1.5} sx={{ height: '100%' }}>
          <CompactChartCard {...paymentChart} delay={220} />
          <CompactChartCard {...secondaryChart} delay={260} />
        </Stack>
      </Grid>
    </Grid>
  );
}

function ExecutiveSummary({ data }) {
  const theme = useTheme();
  const metrics = data?.metrics || {};
  const reports = Boolean(data?.permissions?.can_view_reports);
  const sales = metrics.sales || {};
  const cash = metrics.cash || {};
  const inventory = metrics.inventory || {};
  const productTotal = Number(String(sales.product_sales_total || '').replace(/[^\d.-]/g, '')) || 0;
  const groomingTotal = Number(String(sales.grooming_sales_total || '').replace(/[^\d.-]/g, '')) || 0;
  const revenueTotal = productTotal + groomingTotal;
  const groomingShare = reports && revenueTotal > 0 ? Math.round((groomingTotal / revenueTotal) * 100) : null;
  const topProduct = safeArray(data?.rankings?.top_products)[0]?.label;

  const cards = [
    {
      title: 'Ventas del mes',
      value: reports ? (sales.month_total || 'Q 0.00') : `${sales.count || 0} ventas`,
      description: reports ? `${sales.previous_month_total ? `${sales.previous_month_total} mes anterior` : 'Sin comparativo'}` : 'Actividad comercial visible sin montos.',
      color: theme.palette.info.main,
    },
    {
      title: 'Productos vs grooming',
      value: reports && groomingShare !== null ? `${groomingShare}% grooming` : `${sales.count || 0} tickets`,
      description: reports
        ? `Productos ${sales.product_sales_total || 'Q 0.00'} · Grooming ${sales.grooming_sales_total || 'Q 0.00'}`
        : 'Distribución visible por actividad.',
      color: theme.palette.primary.main,
    },
    {
      title: 'Efectivo y caja',
      value: `${cash.open_sessions_count || 0} abiertas`,
      description: cash.pending_cash_amount
        ? `${cash.pending_cash_amount} estimados pendientes`
        : `${cash.sessions_open_since_yesterday || 0} sesiones abiertas desde ayer`,
      color: theme.palette.warning.main,
    },
    {
      title: 'Inventario en riesgo',
      value: `${inventory.critical_stock_count || 0} críticos`,
      description: `${inventory.critical_with_recent_sales_count || 0} críticos con ventas recientes${topProduct ? ` · Lidera ${topProduct}` : ''}`,
      color: theme.palette.error.main,
    },
  ];

  return (
    <DashboardCard delay={305}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
        <Stack spacing={1.7}>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: 18 }}>Resumen ejecutivo</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>Lecturas accionables del mes, no estados internos.</Typography>
          </Box>
          <Grid container spacing={1.25}>
            {cards.map((card) => (
              <Grid item xs={12} sm={6} key={card.title}>
                <Box sx={{ p: 1.5, borderRadius: 2.2, border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, bgcolor: alpha(card.color, 0.045), height: '100%' }}>
                  <Stack spacing={0.6}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: card.color }} />
                      <Typography sx={{ fontWeight: 850, fontSize: 13 }}>{card.title}</Typography>
                    </Stack>
                    <Typography sx={{ fontWeight: 950, fontSize: 21, letterSpacing: '-0.03em' }}>{card.value}</Typography>
                    <Typography color="text.secondary" sx={{ fontSize: 12.4, lineHeight: 1.4 }}>{card.description}</Typography>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}

function SignalsSection({ signals }) {
  const theme = useTheme();
  const rows = safeArray(signals).slice(0, 6);
  const styles = {
    danger: { color: theme.palette.error.main, icon: ErrorOutlineOutlined },
    warning: { color: theme.palette.warning.main, icon: ReportProblemOutlined },
    success: { color: theme.palette.success.main, icon: HealthAndSafetyOutlined },
    info: { color: theme.palette.info.main, icon: InsightsOutlined },
  };

  return (
    <DashboardCard delay={300}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
        <Stack spacing={1.6}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: 18 }}>Señales importantes</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>Alertas procesadas desde datos reales del periodo.</Typography>
          </Box>
          {rows.length ? rows.map((signal) => {
            const style = styles[signal.type] || styles.info;
            const Icon = style.icon;
            return (
              <Stack key={`${signal.area}-${signal.title}-${signal.description}`} direction="row" spacing={1.2} sx={{ p: 1.35, borderRadius: 2.2, border: `1px solid ${alpha(theme.palette.divider, 0.55)}`, bgcolor: alpha(style.color, 0.055) }}>
                <Box sx={{ width: 34, height: 34, borderRadius: '50%', display: 'grid', placeItems: 'center', color: style.color, bgcolor: alpha(style.color, 0.11), flexShrink: 0 }}><Icon sx={{ fontSize: 18 }} /></Box>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Tooltip title={signal.title || ''}>
                      <Typography sx={{ fontWeight: 600, fontSize: 13.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {signal.title}
                      </Typography>
                    </Tooltip>
                    <Chip size="small" label={signal.area} variant="outlined" sx={{ height: 20, fontSize: 10.5, fontWeight: 500 }} />
                  </Stack>
                  <Tooltip title={signal.description || ''}>
                    <Typography color="text.secondary" sx={{ mt: 0.2, fontSize: 12.8, overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                      {signal.description}
                    </Typography>
                  </Tooltip>
                </Box>
              </Stack>
            );
          }) : <EmptyState title="Sin señales" caption="No hay alertas visibles con tus permisos para este periodo." />}
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}

function RankingList({ title, rows, delay }) {
  const theme = useTheme();
  if (!safeArray(rows).length) return null;
  return (
    <DashboardCard delay={delay}>
      <CardContent sx={{ p: 2.2, '&:last-child': { pb: 2.2 } }}>
        <Stack spacing={1.4}>
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>{title}</Typography>
          {rows.slice(0, 5).map((row, index) => (
            <Stack key={`${title}-${row.label}-${index}`} direction="row" spacing={1.2} alignItems="center">
              <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.primary.main, 0.09), color: 'primary.main', fontWeight: 600, fontSize: 12 }}>{index + 1}</Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Tooltip title={row.label || ''}>
                  <Typography noWrap sx={{ fontWeight: 600, fontSize: 13.2 }}>{row.label}</Typography>
                </Tooltip>
                <Tooltip title={row.secondary || ''}>
                  <Typography noWrap color="text.secondary" sx={{ fontSize: 12 }}>{truncateText(row.secondary, 30)}</Typography>
                </Tooltip>
              </Box>
              <Tooltip title={row.metric || ''}>
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{truncateText(row.metric, 20)}</Typography>
              </Tooltip>
            </Stack>
          ))}
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}

function RankingsCompact({ rankings }) {
  const cards = [
    ['Top productos', rankings?.top_products],
    ['Top marcas', rankings?.top_brands],
    ['Top categorías', rankings?.top_categories],
    ['Top grooming', rankings?.top_grooming_services],
  ].filter(([, rows]) => safeArray(rows).length);

  if (!cards.length) return null;
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1.2 }}>Rankings compactos</Typography>
      <Grid container spacing={1.5}>
        {cards.map(([title, rows], index) => (
          <Grid item xs={12} md={6} xl={3} key={title}>
            <RankingList title={title} rows={rows} delay={330 + index * 35} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function CustomersRecoveryCard({ metrics, reports }) {
  const theme = useTheme();
  const customers = metrics?.customers || {};
  const rows = safeArray(customers.to_recover).slice(0, 5);
  if (!rows.length && !customers.dormant_count) return null;

  return (
    <DashboardCard delay={325}>
      <CardContent sx={{ p: { xs: 2, md: 2.4 }, '&:last-child': { pb: { xs: 2, md: 2.4 } } }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
            <Box>
              <Typography sx={{ fontWeight: 900, fontSize: 18 }}>Clientes por recuperar</Typography>
              <Typography color="text.secondary" sx={{ fontSize: 13 }}>
                {customers.new_month_count || 0} nuevos · {customers.recurrent_count || 0} recurrentes · {customers.dormant_count || 0} dormidos
              </Typography>
            </Box>
            <Chip size="small" color={customers.dormant_count ? 'warning' : 'success'} variant="outlined" label={`${customers.dormant_count || 0} sin compra`} />
          </Stack>
          {rows.length ? (
            <Stack spacing={1}>
              {rows.map((row, index) => (
                <Stack key={`${row.label}-${index}`} direction="row" spacing={1.2} alignItems="center" sx={{ p: 1, borderRadius: 2, bgcolor: alpha(theme.palette.warning.main, 0.055), border: `1px solid ${alpha(theme.palette.divider, 0.45)}` }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: alpha(theme.palette.warning.main, 0.14), color: 'warning.dark', fontWeight: 900 }}>{index + 1}</Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontWeight: 850, fontSize: 13.2 }}>{row.label}</Typography>
                    <Typography noWrap color="text.secondary" sx={{ fontSize: 12 }}>
                      {row.days_without_purchase ?? '-'} días sin compra · última {row.last_purchase || 'sin fecha'}
                    </Typography>
                  </Box>
                  {reports && row.historical_total ? <Typography sx={{ fontWeight: 900, fontSize: 12.5 }}>{row.historical_total}</Typography> : null}
                </Stack>
              ))}
            </Stack>
          ) : (
            <EmptyState title="Sin clientes dormidos" caption="No hay clientes por recuperar con la data visible." />
          )}
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}

function OperationalBlocks({ data }) {
  const theme = useTheme();
  const metrics = data?.metrics || {};
  const permissions = data?.permissions || {};
  const blocks = [];

  if (permissions.can_view_sales) {
    const salesInterpretation = metrics.sales?.today_count > 0
      ? `${metrics.sales.today_count} ventas hoy sostienen el ritmo comercial.`
      : 'Sin ventas registradas hoy en el corte visible.';
    blocks.push({
      title: 'Comercial',
      color: theme.palette.info.main,
      lines: [
        ['Hoy', `${metrics.sales?.today_count ?? 0} ventas`],
        ['Mes', metrics.sales?.month_total || `${metrics.sales?.count || 0} ventas`],
        ['Lectura', salesInterpretation],
      ],
    });
  }
  if (permissions.can_view_cash) {
    blocks.push({
      title: 'Caja y cuadres',
      color: theme.palette.warning.main,
      lines: [
        ['Abiertas', metrics.cash?.open_sessions_count ?? 0],
        ['Desde ayer', metrics.cash?.sessions_open_since_yesterday ?? 0],
        ['Pendiente', metrics.cash?.pending_cash_amount || `${metrics.cash?.closed_count || 0} cortes cerrados`],
      ],
    });
  }
  if (permissions.can_view_inventory) {
    blocks.push({
      title: 'Inventario',
      color: theme.palette.primary.main,
      lines: [
        ['Movimiento', `${metrics.inventory?.movements_month_count ?? 0} movimientos`],
        ['Entradas / salidas', `${metrics.inventory?.inbound_count ?? 0} / ${metrics.inventory?.outbound_count ?? 0}`],
        ['Riesgo', `${metrics.inventory?.critical_with_recent_sales_count ?? 0} críticos con venta reciente`],
      ],
    });
  }
  if (permissions.can_view_sales || permissions.can_view_admin) {
    blocks.push({
      title: 'Clientes',
      color: theme.palette.success.main,
      lines: [
        ['Activos', metrics.customers?.active_month_count ?? 0],
        ['Recurrentes / nuevos', `${metrics.customers?.recurrent_count ?? 0} / ${metrics.customers?.new_month_count ?? 0}`],
        ['Por recuperar', metrics.customers?.dormant_count ?? 0],
      ],
    });
  }

  if (!blocks.length) return null;

  return (
    <Box>
      <Typography sx={{ fontWeight: 950, fontSize: 18, mb: 1.2 }}>Operación por área</Typography>
      <Grid container spacing={1.5}>
        {blocks.map((block, index) => (
          <Grid item xs={12} md={6} xl={3} key={block.title}>
            <DashboardCard delay={340 + index * 35}>
              <CardContent sx={{ p: 2.1, '&:last-child': { pb: 2.1 } }}>
                <Stack spacing={1.3}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: block.color }} />
                    <Typography sx={{ fontWeight: 900 }}>{block.title}</Typography>
                  </Stack>
                  {block.lines.map(([label, value]) => (
                    <Stack key={label} direction="row" justifyContent="space-between" spacing={1}>
                      <Typography color="text.secondary" sx={{ fontSize: 12.8 }}>{label}</Typography>
                      <Typography sx={{ fontWeight: 850, fontSize: 12.8, textAlign: 'right' }}>{value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </CardContent>
            </DashboardCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

function OperativeWidgetCard({ widget, index }) {
  const theme = useTheme();
  const toneColors = {
    warning: theme.palette.warning.main,
    positive: theme.palette.success.main,
    success: theme.palette.success.main,
    sales_today: theme.palette.secondary?.main || theme.palette.info.main,
    teal: theme.palette.info.main,
    info: theme.palette.info.main,
    neutral: theme.palette.text.secondary,
  };
  const color = toneColors[widget?.tone] || theme.palette.primary.main;
  const Icon = {
    sales: TrendingUpOutlined,
    grooming: ContentCutOutlined,
    cash: PointOfSaleOutlined,
    inventory: InventoryOutlined,
    customers: HealthAndSafetyOutlined,
  }[widget?.key] || InsightsOutlined;

  return (
    <DashboardCard delay={120 + index * 35}>
      <CardContent sx={{ p: 2.2, '&:last-child': { pb: 2.2 } }}>
        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
            <Box sx={{ width: 42, height: 42, borderRadius: 2.2, display: 'grid', placeItems: 'center', bgcolor: alpha(color, 0.11), color }}>
              <Icon fontSize="small" />
            </Box>
            <Chip size="small" variant="outlined" label={widget?.area || 'Área'} sx={{ height: 22, fontSize: 11 }} />
          </Stack>
          <Box>
            <Typography sx={{ fontWeight: 850, fontSize: 15 }}>{widget?.title}</Typography>
            <Stack direction="row" spacing={0.9} alignItems="baseline" sx={{ mt: 0.6 }}>
              <Typography sx={{ fontWeight: 950, fontSize: 30, letterSpacing: '-0.04em' }}>
                {Number(widget?.primary || 0).toLocaleString('es-GT')}
              </Typography>
              <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>{widget?.primary_label}</Typography>
            </Stack>
            <Typography color="text.secondary" sx={{ mt: 0.5, fontSize: 12.7 }}>{widget?.secondary}</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 12.2 }}>{widget?.caption}</Typography>
          </Box>
          {safeArray(widget?.items).length ? (
            <Stack spacing={0.7}>
              {safeArray(widget.items).slice(0, 3).map((item, itemIndex) => (
                <Stack key={`${widget.key}-${itemIndex}`} direction="row" spacing={1} alignItems="center" sx={{ p: 0.9, borderRadius: 1.8, bgcolor: alpha(color, 0.055), border: `1px solid ${alpha(theme.palette.divider, 0.45)}` }}>
                  <Typography sx={{ minWidth: 42, fontWeight: 850, fontSize: 12.5 }}>{item.time || `${item.days_without_purchase ?? '-'}d`}</Typography>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography noWrap sx={{ fontWeight: 750, fontSize: 12.8 }}>{item.pet || item.label || 'Registro'}</Typography>
                    <Typography noWrap color="text.secondary" sx={{ fontSize: 11.6 }}>{item.customer || item.last_purchase || item.service || ''}</Typography>
                  </Box>
                </Stack>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}

function OperativeActivityChart({ data }) {
  const theme = useTheme();
  const rows = safeArray(data?.activity);
  const hasData = rows.some((row) => Number(row?.count || 0) > 0);

  return (
    <DashboardCard delay={240}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: 18 }}>Actividad visible</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>Conteos operativos del día y del periodo visible, sin montos.</Typography>
          </Box>
          {hasData ? (
            <Box sx={{ height: 250 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} margin={{ top: 10, right: 12, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                  <XAxis dataKey="label" tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: theme.palette.text.secondary }} />
                  <ChartTooltip formatter={(value) => [Number(value).toLocaleString('es-GT'), 'Cantidad']} />
                  <Bar dataKey="count" radius={[10, 10, 4, 4]}>
                    {rows.map((entry, index) => <Cell key={entry.label} fill={CHART_COLORS[index % CHART_COLORS.length]} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <EmptyState title="Sin actividad visible" caption="No hay conteos operativos para tus permisos en este periodo." />
          )}
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}

function OperativePendingCard({ data }) {
  const theme = useTheme();
  const rows = safeArray(data?.today_pending).slice(0, 6);
  const toneColor = (tone) => ({
    warning: theme.palette.warning.main,
    danger: theme.palette.error.main,
    success: theme.palette.success.main,
    info: theme.palette.info.main,
  }[tone] || theme.palette.info.main);

  return (
    <DashboardCard delay={260}>
      <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
        <Stack spacing={1.5}>
          <Box>
            <Typography sx={{ fontWeight: 900, fontSize: 18 }}>Pendientes de hoy</Typography>
            <Typography color="text.secondary" sx={{ fontSize: 13 }}>Máximo 6 señales operativas según permisos.</Typography>
          </Box>
          {rows.length ? rows.map((item) => {
            const color = toneColor(item?.tone);
            return (
              <Stack key={`${item.area}-${item.title}`} direction="row" spacing={1.1} sx={{ p: 1.25, borderRadius: 2.1, border: `1px solid ${alpha(theme.palette.divider, 0.5)}`, bgcolor: alpha(color, 0.055) }}>
                <Box sx={{ width: 9, mt: 0.6, height: 9, borderRadius: '50%', bgcolor: color, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography sx={{ fontWeight: 850, fontSize: 13.2 }}>{item.title}</Typography>
                    <Chip size="small" variant="outlined" label={item.area} sx={{ height: 20, fontSize: 10.5 }} />
                  </Stack>
                  <Typography color="text.secondary" sx={{ fontSize: 12.5 }}>{item.description}</Typography>
                </Box>
              </Stack>
            );
          }) : (
            <EmptyState title="Sin pendientes" caption="No hay pendientes visibles con tus permisos." />
          )}
        </Stack>
      </CardContent>
    </DashboardCard>
  );
}

function OperativeDetailSections({ data }) {
  const theme = useTheme();
  const widgets = safeArray(data?.widgets);
  const byKey = widgets.reduce((acc, widget) => ({ ...acc, [widget.key]: widget }), {});
  const sections = [];

  if (byKey.grooming) {
    sections.push({
      title: 'Agenda grooming operativa',
      color: theme.palette.info.main,
      rows: safeArray(byKey.grooming.items).slice(0, 5).map((item) => ({
        main: `${item.time || '--:--'} · ${item.pet || 'Mascota'}`,
        secondary: `${item.customer || 'Responsable'} · ${item.service || 'Servicio'}`,
        chip: item.status_label || item.status || 'Cita',
      })),
      empty: 'Sin próximas citas grooming visibles.',
    });
  }
  if (byKey.sales) {
    sections.push({
      title: 'Ventas de hoy',
      color: theme.palette.primary.main,
      rows: safeArray(byKey.sales.details?.top_products).slice(0, 5).map((item) => ({
        main: item.label,
        secondary: item.secondary,
        chip: `${item.count || 0}`,
      })),
      empty: 'Sin productos vendidos visibles en el periodo.',
    });
  }
  if (byKey.cash) {
    const details = byKey.cash.details || {};
    sections.push({
      title: 'Caja y seguimiento',
      color: theme.palette.warning.main,
      rows: [
        { main: details.is_cash_open ? 'Caja abierta' : 'Sin caja abierta', secondary: 'Estado operativo visible', chip: details.open_sessions || 0 },
        { main: 'Cortes pendientes', secondary: 'Revisar cierre/recepción sin montos', chip: details.pending_receipt || 0 },
        { main: 'Abiertas desde ayer', secondary: 'Seguimiento de sesiones prolongadas', chip: details.open_since_yesterday || 0 },
      ],
      empty: 'Sin seguimiento de caja visible.',
    });
  }
  if (byKey.customers) {
    sections.push({
      title: 'Clientes por contactar',
      color: theme.palette.success.main,
      rows: safeArray(byKey.customers.items).slice(0, 5).map((item) => ({
        main: item.label,
        secondary: `${item.days_without_purchase ?? '-'} días · última ${item.last_purchase || 'sin fecha'}`,
        chip: `${item.purchases || 0}`,
      })),
      empty: 'Sin clientes dormidos visibles.',
    });
  }
  if (byKey.inventory) {
    const details = byKey.inventory.details || {};
    sections.push({
      title: 'Inventario para revisar',
      color: theme.palette.error.main,
      rows: [
        { main: 'Stock crítico', secondary: 'Productos con baja existencia', chip: details.critical_stock || 0 },
        { main: 'Sin existencia', secondary: 'Productos agotados', chip: details.no_stock || 0 },
        { main: 'Movimientos del mes', secondary: `Entradas ${details.inbound_count || 0} / salidas ${details.outbound_count || 0}`, chip: details.movements_count || 0 },
      ],
      empty: 'Sin revisión de inventario visible.',
    });
  }

  if (!sections.length) return null;

  return (
    <Grid container spacing={1.5}>
      {sections.map((section, index) => (
        <Grid item xs={12} md={6} xl={4} key={section.title}>
          <DashboardCard delay={300 + index * 35}>
            <CardContent sx={{ p: 2.1, '&:last-child': { pb: 2.1 } }}>
              <Stack spacing={1.3}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: section.color }} />
                  <Typography sx={{ fontWeight: 900 }}>{section.title}</Typography>
                </Stack>
                {section.rows.length ? section.rows.map((row, rowIndex) => (
                  <Stack key={`${section.title}-${rowIndex}`} direction="row" spacing={1} alignItems="center" sx={{ p: 1, borderRadius: 1.8, bgcolor: alpha(section.color, 0.045), border: `1px solid ${alpha(theme.palette.divider, 0.45)}` }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography noWrap sx={{ fontWeight: 800, fontSize: 13 }}>{row.main}</Typography>
                      <Typography noWrap color="text.secondary" sx={{ fontSize: 12 }}>{row.secondary}</Typography>
                    </Box>
                    <Chip size="small" label={row.chip} variant="outlined" sx={{ height: 22 }} />
                  </Stack>
                )) : <EmptyState title="Sin datos" caption={section.empty} />}
              </Stack>
            </CardContent>
          </DashboardCard>
        </Grid>
      ))}
    </Grid>
  );
}

function OperativeHome({ data, loading, onRefresh }) {
  if (loading && !data) {
    return (
      <Stack spacing={1.5}>
        <Skeleton height={76} variant="rounded" />
        <Grid container spacing={1.5}>
          {[0, 1, 2, 3].map((key) => (
            <Grid item xs={12} md={6} xl={3} key={key}>
              <Skeleton height={178} variant="rounded" />
            </Grid>
          ))}
        </Grid>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.8}>
      <DashboardCard delay={60}>
        <CardContent sx={{ p: { xs: 2, md: 2.5 }, '&:last-child': { pb: { xs: 2, md: 2.5 } } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Typography sx={{ fontWeight: 950, fontSize: { xs: 24, md: 28 }, letterSpacing: '-0.04em' }}>Tu jornada</Typography>
              <Typography color="text.secondary" sx={{ fontSize: 13.5 }}>
                {data?.period?.label || 'Periodo actual'} · {data?.summary?.subheadline || 'Actividad visible según permisos'}
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Chip size="small" label={`${data?.summary?.visible_areas || 0} áreas visibles`} variant="outlined" />
              {data?.summary?.last_updated ? <Chip size="small" label={`Actualizado ${formatDateTimeCompact(data.summary.last_updated)}`} variant="outlined" /> : null}
              <Button size="small" variant="outlined" startIcon={<RefreshOutlined />} onClick={onRefresh} disabled={loading}>
                Actualizar
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </DashboardCard>

      <Grid container spacing={1.5}>
        {safeArray(data?.widgets).filter((widget) => widget?.key !== 'cash').map((widget, index) => (
          <Grid item xs={12} sm={6} xl={3} key={widget.key || widget.title}>
            <OperativeWidgetCard widget={widget} index={index} />
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={1.5} alignItems="stretch">
        <Grid item xs={12} lg={7}>
          <OperativeActivityChart data={data} />
        </Grid>
        <Grid item xs={12} lg={5}>
          <OperativePendingCard data={data} />
        </Grid>
      </Grid>
      <OperativeDetailSections data={data} />
    </Stack>
  );
}

function Home() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const lastFetchRef = useRef(0);

  const loadDashboard = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError('');
    try {
      const response = await homeService.getHomeDashboard({ period: 'current_month' });
      setData(response);
      lastFetchRef.current = Date.now();
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'No se pudo cargar el dashboard.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const handleFocus = () => {
      if (Date.now() - lastFetchRef.current >= 60000) {
        loadDashboard({ silent: true });
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [loadDashboard]);

  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Buenos días';
    if (h < 19) return 'Buenas tardes';
    return 'Buenas noches';
  }, []);

  const heroName = useMemo(() => getHeroName(user), [user]);
  const mode = getDashboardMode(data);
  const adminData = getAdminDashboard(data);
  const operativeData = getOperativeHome(data);

  return (
    <Box sx={{ width: '100%', pb: 4, minWidth: 0, overflowX: 'hidden' }}>
      <Stack spacing={2.4}>
        <HeroCompact greeting={greeting} name={heroName} />

        {error ? (
          <Alert
            severity="error"
            action={<Button color="inherit" size="small" onClick={() => loadDashboard()}>Reintentar</Button>}
            sx={{ borderRadius: 2.5 }}
          >
            {error}
          </Alert>
        ) : null}

        {mode === 'operative' ? (
          <OperativeHome data={operativeData} loading={loading} onRefresh={() => loadDashboard()} />
        ) : (
          <>
            <DashboardTopBar data={adminData} loading={loading} onRefresh={() => loadDashboard()} />
            <Box sx={HOME_SECTION_WRAP_SX}>
              <KpiGrid kpis={adminData?.kpis} loading={loading && !adminData} />
            </Box>

            {adminData ? (
              <>
                <Box sx={HOME_SECTION_WRAP_SX}>
                  <ChartsSection data={adminData} />
                </Box>
                <Box sx={HOME_SECTION_WRAP_SX}>
                  <Grid container spacing={1.5} alignItems="stretch">
                    <Grid item xs={12} lg={5}>
                      <SignalsSection signals={adminData.signals} />
                    </Grid>
                    <Grid item xs={12} lg={7}>
                      <ExecutiveSummary data={adminData} />
                    </Grid>
                  </Grid>
                </Box>
                <Box sx={HOME_SECTION_WRAP_SX}>
                  <Grid container spacing={1.5} alignItems="stretch">
                    {adminData.permissions?.can_view_grooming ? (
                      <Grid item xs={12} lg={6}>
                        <AgendaGroomingCard metrics={{ ...(adminData.metrics?.grooming || {}), grooming_sales_total: adminData.metrics?.sales?.grooming_sales_total }} delay={325} />
                      </Grid>
                    ) : null}
                    <Grid item xs={12} lg={adminData.permissions?.can_view_grooming ? 6 : 12}>
                      <CustomersRecoveryCard metrics={adminData.metrics} reports={adminData.permissions?.can_view_reports} />
                    </Grid>
                  </Grid>
                </Box>
                <Box sx={HOME_SECTION_WRAP_SX}>
                  <OperationalBlocks data={adminData} />
                </Box>
                <Box sx={HOME_SECTION_WRAP_SX}>
                  <RankingsCompact rankings={adminData.rankings} />
                </Box>
              </>
            ) : null}
          </>
        )}
      </Stack>
    </Box>
  );
}

export default Home;
